import { useState, useEffect } from 'preact/hooks';
import { getDashboardStats, getBalances, getLeaves, getCalendarData, cancelLeave, approveLeave, rejectLeave } from '../lib/api';

// Helper function to format duration with correct unit
function formatDuration(value, unit) {
  const u = unit || 'days';
  const plural = value !== 1 ? 's' : '';
  if (u === 'hours') return `${value} hour${plural}`;
  if (u === 'weeks') return `${value} week${plural}`;
  if (u === 'months') return `${value} month${plural}`;
  return `${value} day${plural}`;
}

// Helper to get unit label
function getUnitLabel(unit) {
  const u = unit || 'days';
  if (u === 'hours') return 'hours';
  if (u === 'weeks') return 'weeks';
  if (u === 'months') return 'months';
  return 'days';
}

export function Dashboard({ user, workspace }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [balances, setBalances] = useState([]);
  const [todayLeaves, setTodayLeaves] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [scheduledLeaves, setScheduledLeaves] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [currentMonth]);

  async function loadData() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1).toISOString().split('T')[0];
      const lastDay = new Date(year, month + 1, 0).toISOString().split('T')[0];

      const [statsData, balancesData, calendarData, pendingData, scheduledData] = await Promise.all([
        getDashboardStats(),
        getBalances(),
        getCalendarData(firstDay, lastDay),
        getLeaves({ status: 'pending' }),
        getLeaves({ status: 'approved' })
      ]);

      setStats(statsData);
      setBalances(balancesData);
      setPendingRequests(pendingData);
      setScheduledLeaves(scheduledData.filter(l => l.start_date >= today));

      // Process calendar data for chart
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const chartData = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const dayLeaves = (calendarData.leaves || []).filter(l =>
          dateStr >= l.start_date && dateStr <= l.end_date
        );
        const dayHolidays = (calendarData.holidays || []).filter(h => h.date === dateStr);
        const isWeekend = [0, 6].includes(new Date(dateStr).getDay());

        chartData.push({
          day: d,
          date: dateStr,
          isToday: dateStr === today,
          isWeekend,
          vacation: dayLeaves.filter(l => l.leave_type === 'Vacation' || l.leave_type === 'Vacation Leave').length,
          sick: dayLeaves.filter(l => l.leave_type === 'Sick' || l.leave_type === 'Sick Leave').length,
          maternity: dayLeaves.filter(l => l.leave_type?.includes('Maternity')).length,
          other: dayLeaves.filter(l => !['Vacation', 'Vacation Leave', 'Sick', 'Sick Leave'].includes(l.leave_type) && !l.leave_type?.includes('Maternity')).length,
          holiday: dayHolidays.length > 0,
          holidayName: dayHolidays[0]?.name
        });
      }
      setMonthData(chartData);

      // Today's leaves
      const todayData = (calendarData.leaves || []).filter(l =>
        today >= l.start_date && today <= l.end_date && l.status === 'approved'
      );
      setTodayLeaves(todayData);

    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  function previousMonth() {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  }

  function nextMonth() {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  }

  async function handleCancelLeave(leaveId) {
    if (!confirm('Are you sure you want to cancel this leave?')) return;
    try {
      await cancelLeave(leaveId);
      loadData();
    } catch (error) {
      console.error('Failed to cancel leave:', error);
      alert('Failed to cancel leave: ' + (error.message || 'Unknown error'));
    }
  }

  async function handleApprove(requestId) {
    try {
      await approveLeave(requestId);
      loadData();
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve: ' + (error.message || 'Unknown error'));
    }
  }

  async function handleReject(requestId) {
    const reason = prompt('Please provide a reason for rejection (optional):');
    if (reason === null) return; // User cancelled prompt
    try {
      await rejectLeave(requestId, reason);
      loadData();
    } catch (error) {
      console.error('Failed to reject:', error);
      alert('Failed to reject: ' + (error.message || 'Unknown error'));
    }
  }

  const monthLabel = currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const maxAbsences = Math.max(...monthData.map(d => d.vacation + d.sick + d.maternity + d.other), 1);

  if (loading) {
    return (
      <div class="loading-screen">
        <div class="spinner" />
      </div>
    );
  }

  const isApprover = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'approver';

  return (
    <div>
      {/* Stats Grid */}
      <div class="stats-grid">
        <StatCard icon="👥" iconBg="#EFF6FF" value={stats?.todayAbsences || 0} label="Out Today" />
        <StatCard icon="⏳" iconBg="#FEF3C7" value={stats?.pendingRequests || 0} label="Pending Requests" />
        <StatCard icon="📅" iconBg="#D1FAE5" value={stats?.upcomingLeaves || 0} label="Upcoming Leaves" />
        <StatCard icon="👤" iconBg="#E0E7FF" value={stats?.totalUsers || 0} label="Team Members" />
      </div>

      {/* Month Chart */}
      <div class="card" style="margin-bottom: 1.5rem;">
        <div class="card-body">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
            <button class="btn btn-sm btn-secondary" onClick={previousMonth}>
              &lt; Previous Month
            </button>
            <h3 style="margin: 0; font-size: 1.125rem;">{monthLabel}</h3>
            <button class="btn btn-sm btn-secondary" onClick={nextMonth}>
              Next Month &gt;
            </button>
          </div>

          {/* Bar Chart */}
          <div class="month-chart">
            <div class="chart-y-axis">
              {[...Array(maxAbsences + 1)].map((_, i) => (
                <div key={i} class="chart-y-label">{maxAbsences - i}</div>
              ))}
            </div>
            <div class="chart-bars">
              {monthData.map(day => (
                <div
                  key={day.day}
                  class={`chart-bar-container ${day.isToday ? 'today' : ''} ${day.isWeekend ? 'weekend' : ''}`}
                  title={day.holiday ? day.holidayName : `${day.date}: ${day.vacation + day.sick + day.maternity + day.other} absent`}
                >
                  <div class="chart-bar-stack" style={{ height: `${((day.vacation + day.sick + day.maternity + day.other) / maxAbsences) * 100}%` }}>
                    {day.sick > 0 && <div class="bar-segment sick" style={{ flex: day.sick }} />}
                    {day.maternity > 0 && <div class="bar-segment maternity" style={{ flex: day.maternity }} />}
                    {day.vacation > 0 && <div class="bar-segment vacation" style={{ flex: day.vacation }} />}
                    {day.other > 0 && <div class="bar-segment other" style={{ flex: day.other }} />}
                  </div>
                  {day.holiday && <div class="holiday-marker" />}
                  <div class="chart-day-label">{day.day}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div class="chart-legend">
            <div class="legend-item"><span class="legend-dot sick" /> Sick day</div>
            <div class="legend-item"><span class="legend-dot maternity" /> Maternity Leave</div>
            <div class="legend-item"><span class="legend-dot vacation" /> Vacation Leave</div>
            <div class="legend-item"><span class="legend-dot holiday" /> Holiday</div>
            <div class="legend-item"><span class="legend-dot today" /> Today</div>
            <div class="legend-item"><span class="legend-dot non-working" /> Non working day</div>
          </div>
        </div>
      </div>

      {/* Today's Leaves */}
      {todayLeaves.length > 0 && (
        <div class="card" style="margin-bottom: 1.5rem;">
          <div class="card-header">
            <h3>
              {new Date().toLocaleDateString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' })}
              <span class="badge badge-approved" style="margin-left: 0.5rem;">Today</span>
            </h3>
          </div>
          <div class="card-body">
            {todayLeaves.map(leave => (
              <div key={leave.id} class="today-leave-item">
                <div class="avatar">
                  {leave.avatar_url ? <img src={leave.avatar_url} alt="" /> : leave.user_name?.charAt(0)}
                </div>
                <div class="leave-info">
                  <div class="leave-type-badge" style={{ background: getLeaveColor(leave.leave_type) }}>
                    {leave.leave_type}
                  </div>
                  <span class="user-name">{leave.user_name}</span>
                  <p class="leave-description">
                    {leave.start_date === leave.end_date
                      ? `Is away for a ${leave.leave_type} on ${formatDate(leave.start_date)}.`
                      : `Is away for a ${leave.leave_type} from ${formatDate(leave.start_date)} to ${formatDate(leave.end_date)}. Both dates included.`
                    }
                  </p>
                  <p class="leave-days">That's {formatDuration(leave.duration_days || 1, leave.leave_type_unit)} out of the office.</p>
                  {leave.reason && <p class="leave-reason"><strong>Reason:</strong> {leave.reason}</p>}
                </div>
                <div class="leave-actions">
                  <div class="approved-by">Approved By: <strong>{leave.approver_name || 'Auto'}</strong></div>
                  {(user?.id === leave.user_id || isApprover) && (
                    <button class="btn btn-sm btn-danger" onClick={() => handleCancelLeave(leave.id)}>
                      Cancel Leave
                    </button>
                  )}
                </div>
              </div>
            ))}
            {todayLeaves.length > 3 && (
              <button class="btn btn-link" style="width: 100%; text-align: center;">
                Show {todayLeaves.length - 3} more
              </button>
            )}
          </div>
        </div>
      )}

      {/* Balances */}
      <div class="card" style="margin-bottom: 1.5rem;">
        <div class="card-header">
          <h3>Your Leave Balances</h3>
        </div>
        <div class="card-body">
          {balances.length > 0 ? (
            <div class="balance-grid">
              {balances.map(balance => (
                <BalanceCard key={balance.id} balance={balance} />
              ))}
            </div>
          ) : (
            <div class="empty-state">
              <div class="empty-state-icon">📊</div>
              <h3>No balances configured</h3>
              <p>Contact your administrator to set up leave types.</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs: Pending Requests / Scheduled Leaves */}
      <div class="card">
        <div class="card-header" style="border-bottom: none; padding-bottom: 0;">
          <div class="tabs">
            <button
              class={`tab ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending Requests ({pendingRequests.length})
            </button>
            <button
              class={`tab ${activeTab === 'scheduled' ? 'active' : ''}`}
              onClick={() => setActiveTab('scheduled')}
            >
              Scheduled Leaves ({scheduledLeaves.length})
            </button>
          </div>
        </div>
        <div class="card-body">
          {activeTab === 'pending' && (
            pendingRequests.length > 0 ? (
              <div class="leave-list">
                {pendingRequests.map(request => (
                  <div key={request.id} class="leave-list-item">
                    <div class="avatar">
                      {request.avatar_url ? <img src={request.avatar_url} alt="" /> : request.user_name?.charAt(0)}
                    </div>
                    <div class="leave-info">
                      <div class="leave-type-badge pending">{request.leave_type_name}</div>
                      <span class="user-name">{request.user_name}</span>
                      <p>{formatDate(request.start_date)} - {formatDate(request.end_date)} ({formatDuration(request.duration_days, request.leave_type_unit)})</p>
                    </div>
                    <div class="leave-actions">
                      {isApprover && (
                        <>
                          <button class="btn btn-sm btn-success" onClick={() => handleApprove(request.id)}>Approve</button>
                          <button class="btn btn-sm btn-danger" onClick={() => handleReject(request.id)}>Reject</button>
                        </>
                      )}
                      <button class="btn btn-sm btn-secondary" onClick={() => handleCancelLeave(request.id)}>Cancel</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div class="empty-state">
                <div class="empty-state-icon">🎉</div>
                <h3>No pending requests</h3>
                <p>There are no pending requests.</p>
              </div>
            )
          )}

          {activeTab === 'scheduled' && (
            scheduledLeaves.length > 0 ? (
              <div class="leave-list">
                {scheduledLeaves.slice(0, 10).map(leave => (
                  <div key={leave.id} class="leave-list-item">
                    <div class="avatar">
                      {leave.avatar_url ? <img src={leave.avatar_url} alt="" /> : leave.user_name?.charAt(0)}
                    </div>
                    <div class="leave-info">
                      <div class="leave-type-badge" style={{ background: getLeaveColor(leave.leave_type_name) }}>
                        {leave.leave_type_name}
                      </div>
                      <span class="user-name">{leave.user_name}</span>
                      <p>{formatDate(leave.start_date)} - {formatDate(leave.end_date)} ({formatDuration(leave.duration_days, leave.leave_type_unit)})</p>
                    </div>
                    {(user?.id === leave.user_id || isApprover) && (
                      <div class="leave-actions">
                        <button class="btn btn-sm btn-danger" onClick={() => handleCancelLeave(leave.id)}>
                          Cancel Leave
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div class="empty-state">
                <div class="empty-state-icon">📅</div>
                <h3>No scheduled leaves</h3>
                <p>No upcoming leaves are scheduled.</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, iconBg, value, label }) {
  return (
    <div class="stat-card">
      <div class="stat-card-icon" style={{ background: iconBg }}>{icon}</div>
      <div class="stat-card-value">{value}</div>
      <div class="stat-card-label">{label}</div>
    </div>
  );
}

function BalanceCard({ balance }) {
  const used = balance.used || 0;
  const entitlement = balance.entitlement || 0;
  const pending = balance.pending || 0;
  const unit = getUnitLabel(balance.unit);
  const isUnlimited = entitlement === -1;
  // For unlimited, available doesn't make sense - show "Unlimited"
  const available = isUnlimited ? 'Unlimited' : (balance.available || 0);

  return (
    <div class="balance-card">
      <div class="balance-icon" style={{ background: `${balance.color}20`, color: balance.color }}>
        {balance.icon || '📅'}
      </div>
      <div class="balance-info">
        <div class="balance-type">{balance.leave_type_name}</div>
        <div class="balance-available">
          {isUnlimited ? 'Unlimited' : available} <span>{isUnlimited ? '' : `${unit} available`}</span>
        </div>
        <div class="balance-details">
          <span>Used: {used} {unit}{isUnlimited ? '' : ` / ${entitlement} ${unit}`}</span>
          {pending > 0 && <span>Pending: {pending} {unit}</span>}
        </div>
      </div>
    </div>
  );
}

function getLeaveColor(type) {
  const colors = {
    'Vacation': '#8B5CF6',
    'Vacation Leave': '#8B5CF6',
    'Sick': '#EF4444',
    'Sick Leave': '#EF4444',
    'Sick day': '#EF4444',
    'Maternity': '#EC4899',
    'Maternity Leave': '#EC4899'
  };
  return colors[type] || '#6B7280';
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { month: 'long', day: 'numeric', year: 'numeric' });
}
